import { INTAKE_COLORS } from "../theme/intakeTokens";

/** Normalize ep-001 / ep-1 to the same id for matching. */
export function normalizeEndpointId(id) {
  const s = String(id || "").trim();
  const m = s.match(/^ep-0*(\d+)$/i);
  return m ? `ep-${m[1]}` : s;
}

/** Match a probe result row to a form endpoint (id first, then method+path). */
export function endpointResultMatches(formEp, result) {
  if (!formEp || !result) return false;
  if (
    normalizeEndpointId(result.endpoint_id) === normalizeEndpointId(formEp.id)
  ) {
    return true;
  }
  const method = String(formEp.method || "GET").toUpperCase();
  const resultMethod = String(result.method || "GET").toUpperCase();
  const path = String(formEp.path || "").trim();
  const resultPath = String(result.path || "").trim();
  if (!path || !resultPath) return false;
  const norm = (p) => (p.startsWith("/") ? p : `/${p}`);
  return resultMethod === method && norm(resultPath) === norm(path);
}

/**
 * When testing one (or some) endpoints, keep prior results for the others.
 * Drops previous rows that match tested form endpoints by id OR method+path
 * so a re-test replaces the old badge instead of leaving a stale result.
 *
 * @param {object|null} prev
 * @param {object|null} incoming
 * @param {string[]|undefined} testedEndpointIds
 * @param {Array<{id?: string, method?: string, path?: string}>} [formEndpoints]
 */
/**
 * Derive overall_status from endpoint rows (after merge) so the toast matches visible results.
 * Accepts both apiAgent (pass / pass_with_warnings / blocked) and HTTP probe (ready / …) labels.
 */
export function recomputeProbeOverallStatus(report) {
  if (!report || typeof report !== "object") return report;
  const endpoints = Array.isArray(report.endpoints) ? report.endpoints : [];
  if (!endpoints.length) return report;

  const agentMode = String(report.probe_mode || "").includes("api_agent");
  const anyBlocking = endpoints.some((ep) => {
    if (ep.blocking === true) return true;
    if (ep.reachable === false) return true;
    if (ep.auth_status === "invalid") return true;
    const flags = Array.isArray(ep.flags) ? ep.flags : [];
    return flags.some((f) => f.severity === "critical");
  });
  const anyReachable = endpoints.some((ep) => ep.reachable === true);
  const anyWarn = endpoints.some((ep) => {
    if (ep.auth_status === "over_privileged" || ep.auth_status === "untested") return true;
    if (["partial", "mismatch", "client_validation", "html_not_api"].includes(ep.schema_status)) {
      return true;
    }
    const sc = ep.status_code;
    if (sc === 400 || sc === 422) return true;
    if (ep.response && ep.response.valid === false) return true;
    return false;
  });

  let overall_status;
  if (!anyReachable || anyBlocking) {
    overall_status = agentMode ? "blocked" : "blocked";
  } else if (anyWarn) {
    overall_status = agentMode ? "pass_with_warnings" : "ready_with_warnings";
  } else {
    overall_status = agentMode ? "pass" : "ready";
  }

  return {
    ...report,
    overall_status,
    gate0_can_proceed: !anyBlocking && anyReachable,
  };
}

export function mergeProbeReports(prev, incoming, testedEndpointIds, formEndpoints) {
  if (!incoming) return prev || null;
  if (!Array.isArray(testedEndpointIds) || testedEndpointIds.length === 0) {
    return recomputeProbeOverallStatus(incoming);
  }
  const testedSet = new Set(testedEndpointIds.map(normalizeEndpointId));
  const testedFormEps = (formEndpoints || []).filter((ep) =>
    testedSet.has(normalizeEndpointId(ep?.id))
  );

  const kept = (prev?.endpoints || []).filter((r) => {
    if (testedSet.has(normalizeEndpointId(r.endpoint_id))) return false;
    if (testedFormEps.some((ep) => endpointResultMatches(ep, r))) return false;
    return true;
  });

  return recomputeProbeOverallStatus({
    ...incoming,
    endpoints: [...kept, ...(incoming.endpoints || [])],
  });
}

/** Remove results for the endpoints about to be re-tested (clear stale UI). */
export function clearProbeResultsForEndpoints(prev, endpointIds, formEndpoints) {
  if (!prev) return prev;
  if (!Array.isArray(endpointIds) || endpointIds.length === 0) {
    return { ...prev, endpoints: [] };
  }
  const testedSet = new Set(endpointIds.map(normalizeEndpointId));
  const testedFormEps = (formEndpoints || []).filter((ep) =>
    testedSet.has(normalizeEndpointId(ep?.id))
  );
  return {
    ...prev,
    endpoints: (prev.endpoints || []).filter((r) => {
      if (testedSet.has(normalizeEndpointId(r.endpoint_id))) return false;
      if (testedFormEps.some((ep) => endpointResultMatches(ep, r))) return false;
      return true;
    }),
  };
}

export function probeResultKey(epId, payloadName) {
  return payloadName ? `${epId}::${payloadName}` : epId;
}

export function statusChipColor(statusCode) {
  if (statusCode == null) return INTAKE_COLORS.red;
  if (statusCode >= 200 && statusCode < 300) return INTAKE_COLORS.green;
  if (statusCode >= 400 && statusCode < 500) return INTAKE_COLORS.amber;
  return INTAKE_COLORS.red;
}

function normalizeEndpointPath(path) {
  const value = String(path ?? "").trim();
  if (!value) return "";
  return value.startsWith("/") ? value : `/${value}`;
}

function sameProbeValue(left, right) {
  return String(left ?? "").trim() === String(right ?? "").trim();
}

function urlMatchesBase(url, baseUrl) {
  const base = String(baseUrl || "").trim().replace(/\/+$/, "");
  const target = String(url || "").trim();
  if (!base || !target) return true;
  if (target === base) return true;
  return target.startsWith(`${base}/`);
}

/**
 * Non-secret identity of one endpoint plus the API base URL.
 * Bearer token edits do not change this fingerprint.
 */
export function endpointConfigFingerprint(apiForm = {}, ep = {}) {
  const payloads = Array.isArray(ep.payloads)
    ? ep.payloads.map((payload) => ({
        name: String(payload?.name ?? "").trim(),
        body: String(payload?.body ?? payload?.requestBody ?? "").trim(),
      }))
    : [];
  return [
    String(apiForm.baseUrl ?? "").trim().replace(/\/+$/, ""),
    String(ep.method || "").trim().toUpperCase(),
    normalizeEndpointPath(ep.path),
    String(ep.requestBody ?? "").trim(),
    JSON.stringify(payloads),
  ].join("\u0001");
}

/** Stamp freshly tested rows. Rows from other endpoints keep their previous fingerprint. */
export function tagProbeReportWithForm(report, apiForm, testedEndpointIds) {
  if (!report || typeof report !== "object") return report;
  const tested =
    Array.isArray(testedEndpointIds) && testedEndpointIds.length
      ? new Set(testedEndpointIds.map(normalizeEndpointId))
      : null;
  return {
    ...report,
    endpoints: (report.endpoints || []).map((row) => {
      if (tested && !tested.has(normalizeEndpointId(row.endpoint_id))) return row;
      const ep = (apiForm?.endpoints || []).find((formEp) => endpointResultMatches(formEp, row));
      if (!ep) return row;
      return {
        ...row,
        config_fingerprint: endpointConfigFingerprint(apiForm, ep),
      };
    }),
  };
}

/**
 * True when this probe row still belongs to the endpoint form on screen.
 * Empty method, path, and base URL match, so hydration does not mark a saved result stale.
 */
export function endpointProbeIsCurrent(result, ep, apiForm = {}) {
  if (!endpointResultMatches(ep, result)) return false;
  const formBase = String(apiForm.baseUrl ?? "").trim();
  const formPath = String(ep?.path ?? "").trim();
  const formMethod = String(ep?.method ?? "").trim();
  if (!formBase && !formPath && !formMethod) return true;
  if (result?.config_fingerprint) {
    return result.config_fingerprint === endpointConfigFingerprint(apiForm, ep);
  }
  if (
    formMethod &&
    result.method &&
    !sameProbeValue(formMethod.toUpperCase(), String(result.method).toUpperCase())
  ) {
    return false;
  }
  if (
    formPath &&
    result.path &&
    normalizeEndpointPath(formPath) !== normalizeEndpointPath(result.path)
  ) {
    return false;
  }
  if (formBase && result.url && !urlMatchesBase(result.url, formBase)) return false;
  return true;
}

/** Drop endpoint rows that belong to a previous URL, method, path, or request body. */
export function probeReportForForm(report, apiForm) {
  if (!report || typeof report !== "object") return report || null;
  const formEps = Array.isArray(apiForm?.endpoints) ? apiForm.endpoints : [];
  const hasIdentity =
    Boolean(String(apiForm?.baseUrl || "").trim()) ||
    formEps.some((ep) => String(ep?.path || ep?.method || "").trim());
  if (!hasIdentity) return report;
  const kept = (report.endpoints || []).filter((row) =>
    formEps.some((ep) => endpointProbeIsCurrent(row, ep, apiForm))
  );
  if (!report.endpoints || kept.length === report.endpoints.length) return report;
  if (!kept.length) return null;
  return recomputeProbeOverallStatus({ ...report, endpoints: kept });
}

/**
 * Argument for declared system checks.
 * A mismatched saved probe must not fall through as the current endpoint result.
 */
export function connectionReportForDeclaredChecks(report, savedSummary, apiForm) {
  if (report) return probeReportForForm(report, apiForm) || { overall_status: "stale", endpoints: [] };
  if (!savedSummary || probeReportForForm(savedSummary, apiForm)) return null;
  return { overall_status: "stale", endpoints: [] };
}
